<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'MasterData',
  },
})

const ability = useAbility()

const items = ref([])
const loading = ref(false)
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(15)
const search = ref('')

const dialog = ref(false)
const dialogTitle = ref('')
const editingId = ref(null)
const saving = ref(false)
const errors = ref({})

const formDefault = () => ({ name: '', category: '', contact_person: '', phone: '', email: '', address: '', is_active: true })
const form = ref(formDefault())

const deleteDialog = ref(false)
const deletingItem = ref(null)
const deleting = ref(false)

const fetchItems = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value) query.search = search.value

    const response = await useApi('/api/v1/master-data/vendors', { query }).get()
    const data = response?.data ?? response

    items.value = Array.isArray(data) ? data : (data?.data ?? [])
    totalItems.value = data?.meta?.total ?? data?.total ?? 0
  } catch (e) {
    console.error('Fetch vendors error:', e)
  } finally {
    loading.value = false
  }
}

onMounted(fetchItems)
watch(search, () => { page.value = 1; fetchItems() })
watch(page, fetchItems)

const openCreate = () => {
  editingId.value = null
  form.value = formDefault()
  errors.value = {}
  dialogTitle.value = 'Tambah Vendor'
  dialog.value = true
}

const openEdit = item => {
  editingId.value = item.id
  form.value = {
    name: item.name,
    category: item.category ?? '',
    contact_person: item.contact_person ?? '',
    phone: item.phone ?? '',
    email: item.email ?? '',
    address: item.address ?? '',
    is_active: item.is_active,
  }
  errors.value = {}
  dialogTitle.value = 'Edit Vendor'
  dialog.value = true
}

const save = async () => {
  saving.value = true
  errors.value = {}
  try {
    const api = useApi(editingId.value ? `/api/v1/master-data/vendors/${editingId.value}` : '/api/v1/master-data/vendors')
    const response = editingId.value ? await api.put({ body: form.value }) : await api.post({ body: form.value })
    const saved = response?.data ?? response

    if (editingId.value) {
      const idx = items.value.findIndex(i => i.id === editingId.value)
      if (idx !== -1) items.value[idx] = saved
    } else {
      fetchItems()
    }
    dialog.value = false
  } catch (e) {
    if (e?.status === 422) errors.value = e?.data?.errors ?? {}
    else console.error('Save vendor error:', e)
  } finally {
    saving.value = false
  }
}

const confirmDelete = item => { deletingItem.value = item; deleteDialog.value = true }

const doDelete = async () => {
  deleting.value = true
  try {
    await useApi(`/api/v1/master-data/vendors/${deletingItem.value.id}`).delete()
    deleteDialog.value = false
    fetchItems()
  } catch (e) {
    console.error('Delete vendor error:', e)
  } finally {
    deleting.value = false
  }
}

const headers = [
  { title: 'Nama', key: 'name' },
  { title: 'Kategori', key: 'category' },
  { title: 'Kontak', key: 'contact_person' },
  { title: 'Telepon', key: 'phone' },
  { title: 'Status', key: 'is_active', align: 'center', width: 100 },
  { title: 'Aksi', key: 'actions', align: 'center', sortable: false, width: 120 },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">
              Vendor
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Master data pihak ketiga (vendor/rekanan)
            </p>
          </div>
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            prepend-icon="ri-add-line"
            @click="openCreate"
          >
            Tambah Vendor
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <VCard class="mb-4">
      <VCardText>
        <VTextField
          v-model="search"
          placeholder="Cari nama atau kontak..."
          prepend-inner-icon="ri-search-line"
          clearable
          hide-details
          density="compact"
          style="max-width: 320px;"
        />
      </VCardText>
    </VCard>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="perPage"
        hide-default-footer
      >
        <template #item.is_active="{ item }">
          <VChip
            size="small"
            :color="item.is_active ? 'success' : 'secondary'"
          >
            {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
          </VChip>
        </template>
        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            icon="ri-pencil-line"
            variant="text"
            size="small"
            color="primary"
            @click="openEdit(item)"
          />
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            icon="ri-delete-bin-line"
            variant="text"
            size="small"
            color="error"
            @click="confirmDelete(item)"
          />
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada data
            </p>
          </div>
        </template>
      </VDataTable>
      <VDivider />
      <VCardText class="d-flex align-center justify-space-between flex-wrap gap-3 pa-4">
        <span class="text-body-2 text-medium-emphasis">Total: {{ totalItems }}</span>
        <VPagination
          v-if="totalItems > perPage"
          v-model="page"
          :length="Math.ceil(totalItems / perPage)"
          :total-visible="5"
          density="compact"
        />
      </VCardText>
    </VCard>

    <VDialog
      v-model="dialog"
      max-width="600"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          {{ dialogTitle }}
        </VCardTitle>
        <VCardText class="pa-6">
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.name"
                label="Nama Vendor *"
                :error-messages="errors.name"
                autofocus
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.category"
                label="Kategori"
                :error-messages="errors.category"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.contact_person"
                label="Kontak Person"
                :error-messages="errors.contact_person"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.phone"
                label="Telepon"
                :error-messages="errors.phone"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.email"
                label="Email"
                type="email"
                :error-messages="errors.email"
              />
            </VCol>
            <VCol cols="12">
              <VTextarea
                v-model="form.address"
                label="Alamat"
                rows="2"
                :error-messages="errors.address"
              />
            </VCol>
            <VCol cols="12">
              <VSwitch
                v-model="form.is_active"
                label="Aktif"
                color="success"
                hide-details
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="dialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="save"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="deleteDialog"
      max-width="420"
    >
      <VCard>
        <VCardTitle class="pa-6 pb-2">
          Hapus Vendor?
        </VCardTitle>
        <VCardText class="pa-6 pt-0">
          <strong>{{ deletingItem?.name }}</strong> akan dihapus.
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="deleting"
            @click="deleteDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="error"
            :loading="deleting"
            @click="doDelete"
          >
            Hapus
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
